import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem
} from '@mui/material';
import api, { ApiResponse } from '../../services/api';

export interface DevSecOpsComponentDto {
  _id: string;
  key: string;
  page: string;
  title: string;
  subtitle?: string;
  contentType: 'richText' | 'list' | 'checklist' | 'linkList';
  content: any;
  tags?: string[];
  linkedChapters?: string[];
  maturityLevel?: string;
  dataClassification?: string;
  requiresMfa?: boolean;
  category?: 'platform' | 'pipeline' | 'security' | 'observability' | 'release' | 'accessibility' | string;
}

interface DevSecOpsComponentCardProps {
  component: DevSecOpsComponentDto;
  currentUserRole?: string;
  onUpdated?: (updated: DevSecOpsComponentDto) => void;
}

const DevSecOpsComponentCard: React.FC<DevSecOpsComponentCardProps> = ({
  component,
  currentUserRole,
  onUpdated
}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(component.title);
  const [subtitle, setSubtitle] = useState(component.subtitle || '');
  const [contentText, setContentText] = useState(
    typeof component.content?.text === 'string' ? component.content.text : ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>(component.category || '');

  const categoryColor: Record<string, string> = {
    platform: '#003366', // USCIS blue
    pipeline: '#005ea2',
    security: '#B31B1B',
    observability: '#2e8540',
    release: '#2b3990',
    accessibility: '#744210',
    operations: '#5c3c92',
    transition: '#1d70b8',
    governance: '#5a2900',
    quality: '#0b5f4a'
  };

  const normalizedCategory = (component.category || '').toLowerCase();
  const borderColor = categoryColor[normalizedCategory] || '#003366';

  const normalizedRole = (currentUserRole || '').toLowerCase();
  const canEdit =
    normalizedRole.includes('admin') ||
    normalizedRole.includes('data steward') ||
    normalizedRole.includes('data-steward');

  const handleOpen = () => {
    if (!canEdit) return;
    setOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!canEdit) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await api.put<ApiResponse<DevSecOpsComponentDto>>(
        `/devsecops/components/${component.key}`,
        {
          title,
          subtitle,
          category: category || undefined,
          contentType: 'richText',
          content: { text: contentText }
        }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(
          (response.data as any).message ||
            (response.data as any).error ||
            'Failed to save component'
        );
      }

      const updated = response.data.data;
      if (onUpdated) {
        onUpdated(updated as DevSecOpsComponentDto);
      }
      setOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save component');
    } finally {
      setSaving(false);
    }
  };

  const renderContent = () => {
    if (component.contentType === 'list' && Array.isArray(component.content?.items)) {
      return (
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          {component.content.items.map((item: string, idx: number) => (
            <li key={idx}>
              <Typography variant="body2">{item}</Typography>
            </li>
          ))}
        </Box>
      );
    }

    const text =
      typeof component.content?.text === 'string'
        ? component.content.text
        : typeof component.content?.html === 'string'
        ? component.content.html
        : '';

    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ whiteSpace: 'pre-line' }}
      >
        {text}
      </Typography>
    );
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          cursor: canEdit ? 'pointer' : 'default',
          borderLeft: `6px solid ${borderColor}`
        }}
        tabIndex={canEdit ? 0 : -1}
        role={canEdit ? 'button' : 'article'}
        aria-label={
          canEdit
            ? `Edit DevSecOps component: ${component.title}`
            : component.title
        }
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
      >
        <CardHeader
          title={
            <Typography variant="h6" component="h3" fontWeight="bold">
              {component.title}
            </Typography>
          }
          subheader={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {component.subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {component.subtitle}
                </Typography>
              )}
              {normalizedCategory && (
                <Chip
                  label={
                    normalizedCategory.charAt(0).toUpperCase() +
                    normalizedCategory.slice(1)
                  }
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: borderColor,
                    color: '#ffffff',
                    fontWeight: 500
                  }}
                />
              )}
            </Box>
          }
        />
        <CardContent>
          {renderContent()}

          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {component.linkedChapters?.map((chapter) => (
                <Chip
                  key={chapter}
                  label={chapter}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              ))}
              {component.tags?.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>Edit DevSecOps Component</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
            <TextField
              label="Subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              fullWidth
            />
            <TextField
              label="Category"
              select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              helperText="Select the primary category for this capability"
            >
              <MenuItem value="platform">Platform</MenuItem>
              <MenuItem value="pipeline">Pipeline</MenuItem>
              <MenuItem value="security">Security</MenuItem>
              <MenuItem value="observability">Observability</MenuItem>
              <MenuItem value="release">Release</MenuItem>
              <MenuItem value="accessibility">Accessibility</MenuItem>
              <MenuItem value="operations">Operations</MenuItem>
              <MenuItem value="transition">Transition</MenuItem>
              <MenuItem value="governance">Governance</MenuItem>
              <MenuItem value="quality">Quality</MenuItem>
            </TextField>
            <TextField
              label="Content"
              value={contentText}
              onChange={(e) => setContentText(e.target.value)}
              fullWidth
              multiline
              minRows={4}
            />
            {error && (
              <Typography color="error" role="alert">
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} variant="contained">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DevSecOpsComponentCard;
